<script lang="ts" setup>
  import { useTimeoutFn } from '@vueuse/core';
  import QrcodeVue from 'qrcode.vue';
  import { computed, ref } from 'vue';
  import type { Player } from '../../../../shared/api/player.ts';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import { activePlayers } from './activePlayers.ts';
  import CardGameCode from './CardGameCode.vue';

  const props = withDefaults(
    defineProps<{
      player: Player;
      players: Player[];
      gameCode: number;
      showInviteFirst?: boolean;
      invitationShownForMs?: number;
    }>(),
    { showInviteFirst: false, invitationShownForMs: 20_000 },
  );

  const isFlipped = ref(props.showInviteFirst);

  const flipToThePlayers = () => {
    isFlipped.value = false;
    startUserAction(userAction.lobbyInvitationFlipped);
  };

  const { stop: keepTheInvitation } = useTimeoutFn(flipToThePlayers, props.invitationShownForMs, {
    immediate: props.showInviteFirst,
  });

  const toggleFlip = () => {
    keepTheInvitation();
    isFlipped.value = !isFlipped.value;
  };

  const otherPlayers = computed(() => {
    return props.players.filter((player) => player.id !== props.player.id);
  });

  const activePlayerCount = computed(() => activePlayers(props.players).length);

  const joinUrl = computed(() => {
    return `${window.location.origin}/code/${props.gameCode}`;
  });
</script>

<template>
  <div id="card-container" :class="{ flipped: isFlipped }">
    <div id="card-inner-flippable">
      <div id="card-front" class="card-face">
        <div id="card-title">
          <CardGameCode :aria-hidden="isFlipped" :game-code="gameCode" />
          <h2>Players ({{ activePlayerCount }})</h2>
          <button
            :data-faro-user-action-name="userAction.lobbyFlipInvite"
            class="flip-button"
            type="button"
            @click="toggleFlip"
          >
            <svg
              fill="none"
              height="24"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect height="5" rx="1" width="5" x="3" y="3" />
              <rect height="5" rx="1" width="5" x="16" y="3" />
              <rect height="5" rx="1" width="5" x="3" y="16" />
              <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
              <path d="M21 21v.01" />
              <path d="M12 7v3a2 2 0 0 1-2 2H7" />
              <path d="M3 12h.01" />
              <path d="M12 3h.01" />
              <path d="M12 16v.01" />
              <path d="M16 12h1" />
              <path d="M21 12v.01" />
              <path d="M12 21v.01" />
            </svg>
            <span>Invite</span>
          </button>
        </div>
        <div id="card-content">
          <div id="pinned-player">{{ player.nickname }}</div>
          <hr class="divider" />
          <ul id="player-list" tabindex="0">
            <li
              v-for="gamePlayer in otherPlayers"
              :key="gamePlayer.id"
              :data-inactive="!gamePlayer.active"
              class="player-name"
            >
              {{ gamePlayer.nickname }}
            </li>
          </ul>
        </div>
      </div>

      <div id="card-back" class="card-face">
        <div id="card-title">
          <CardGameCode :aria-hidden="!isFlipped" :game-code="gameCode" />
          <h2>Scan to join</h2>
          <button
            :data-faro-user-action-name="userAction.lobbyFlipPlayers"
            class="flip-button"
            type="button"
            @click="toggleFlip"
          >
            <svg
              fill="none"
              height="24"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line x1="8" x2="21" y1="6" y2="6" />
              <line x1="8" x2="21" y1="12" y2="12" />
              <line x1="8" x2="21" y1="18" y2="18" />
              <line x1="3" x2="3.01" y1="6" y2="6" />
              <line x1="3" x2="3.01" y1="12" y2="12" />
              <line x1="3" x2="3.01" y1="18" y2="18" />
            </svg>
            <span>Players</span>
          </button>
        </div>
        <div id="card-content" class="qr-container">
          <QrcodeVue :size="200" :value="joinUrl" aria-label="QR code to join the game" level="H" />
          <div class="join-link">{{ joinUrl }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  #card-container {
    flex: 1 1 22rem;
    max-width: var(--control-max-width);
    min-height: 0;
    perspective: 1000px;
    width: 100%;
  }

  #card-inner-flippable {
    height: 100%;
    position: relative;
    text-align: center;
    transform-style: preserve-3d;
    transition: transform 0.6s;
    width: 100%;
  }

  #card-container.flipped #card-inner-flippable {
    transform: rotateY(180deg);
  }

  .card-face {
    backface-visibility: hidden;
    background: var(--color-surface);
    border-radius: var(--radius-card);
    display: flex;
    flex-direction: column;
    height: 100%;
    outline: var(--color-primary) solid 2px;
    position: absolute;
    transform-style: flat;
    width: 100%;
  }

  #card-front {
    transform: rotateY(0deg);
    z-index: 2;
  }

  #card-back {
    transform: rotateY(180deg);
  }

  #card-title {
    align-items: center;
    background: var(--color-primary);
    border-top-left-radius: var(--radius-card);
    border-top-right-radius: var(--radius-card);
    color: var(--color-on-primary);
    display: flex;
    justify-content: space-between;
    padding: 0.35rem 0.75rem;
    width: 100%;
  }

  #card-title h2 {
    font-size: var(--font-size-lg);
  }

  .flip-button {
    align-items: center;
    background: var(--color-button-on-primary);
    border: none;
    border-radius: var(--radius-full);
    color: var(--color-on-primary);
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: var(--font-size-xs);
    font-weight: 700;
    gap: 0.3rem;
    line-height: 1;
    padding: 0.3rem 0.625rem 0.3rem 0.45rem;
    transition: transform 0.15s;
    white-space: nowrap;
  }

  .flip-button:active {
    transform: scale(0.92);
  }

  .flip-button svg {
    flex-shrink: 0;
    height: 1rem;
    width: 1rem;
  }

  .flip-button > * {
    pointer-events: none;
  }

  #card-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
  }

  .qr-container {
    align-items: center;
    gap: 1rem;
    justify-content: center;
    padding: 1rem;
  }

  .join-link {
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
    padding: 0 1rem;
    word-break: break-all;
  }

  #player-list {
    flex: 1;
    list-style: none;
    margin: 0;
    overflow-y: auto;
    padding: 0;
    scrollbar-color: var(--color-primary) var(--color-scrollbar-track);
    scrollbar-width: thin;
  }

  #player-list::-webkit-scrollbar {
    width: 0.4rem;
  }

  #player-list::-webkit-scrollbar-track {
    background: var(--color-scrollbar-track);
    border-radius: var(--radius-sm);
  }

  #player-list::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: var(--radius-sm);
  }

  #player-list::-webkit-scrollbar-thumb:hover {
    background: color-mix(in srgb, var(--color-primary), var(--color-tint) 20%);
  }

  #pinned-player {
    color: var(--color-primary);
    font-weight: var(--font-weight-strong);
    margin: 0;
    padding: 0.5rem 0;
  }

  .divider {
    border: none;
    border-top: 1px solid var(--color-divider);
    margin: 0 auto;
    width: 80%;
  }

  .player-name {
    margin: 0.5rem 0;
  }

  [data-inactive='true'] {
    color: var(--color-text-inactive);
  }
</style>
